import {
  InternalRefetchQueryDescriptor,
  useApolloClient,
} from "@apollo/client";
import { assertNever, notNull } from "@msys/common";
import { TFunction } from "@msys/tolgee";
import { LoadingSpinner, Modal } from "@msys/ui";
import { Box, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, FormikProps, useFormikContext } from "formik";
import { isEqual, isNil, isNull, omit } from "lodash-es";
import debounce from "lodash.debounce";
import differenceWith from "lodash.differencewith";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import {
  Props2Number,
  Props2NumberArray,
  Props2NumberRange,
  SapProductKmatVariantInputProp,
} from "../../../clients/graphqlTypes.js";
import { DatePickerField } from "../../commons/form-fields/DatePickerField.js";
import { FormattedPriceField } from "../../commons/form-fields/FormattedPriceField.js";
import { TextField } from "../../commons/form-fields/TextField.js";
import { TimePickerField } from "../../commons/form-fields/TimePickerField.js";
import { Props2NonComputedAllFragment } from "../doc-items/properties.generated.js";
import {
  isValueNumberInAllowedValuesNumber,
  isValueNumberInNumberRange,
  isValueNumberInNumberRangeStep,
} from "../doc-items/properties.js";
import { usePropertyHelpers } from "../doc-items/usePropertyHelpers.js";
import { WizardPropertyField } from "../doc-items/WizardPropertyField.js";
import {
  useS4HanaKmatProductActivateVariantMutation,
  useS4HanaKmatProductCreateVariantDraftMutation,
  useS4HanaKmatProductSetItemProductFromSapProductMutation,
  useS4HanaKmatProductSetVariantPropValueMutation,
} from "./S4HanaKmatProductConfigureModal.generated.js";

const MANUFACTURER_ARTICLE_NUMBER_MAX_LENGTH = 35;

type FormValues = { manufacturerArticleNumber: string; netPrice: number } & {
  [key: string]: Props2NonComputedAllFragment;
};

interface Props {
  projectId: string;
  quoteId: string;
  itemId: string;
  productId: string;
  manufacturerArticleNumber: string | null | undefined;
  netPrice: number | null | undefined;
  refetchQueries?: InternalRefetchQueryDescriptor[];
  handleClose: () => void;
}
export const S4HanaKmatProductConfigModal = ({
  projectId,
  quoteId,
  itemId,
  productId,
  manufacturerArticleNumber: initialManufacturerArticleNumber,
  netPrice: initialNetPrice,
  refetchQueries,
  handleClose,
}: Props) => {
  const { t } = useTranslate(["Global", "SapS4Hana", "Validation"]);
  const { getNumberLabel } = usePropertyHelpers();
  const { enqueueSnackbar } = useSnackbar();

  const [properties, setProperties] =
    React.useState<Array<SapProductKmatVariantInputProp> | null>(null);
  const [isUpdating, setIsUpdating] = React.useState<boolean>(false);
  const [isValid, setValidity] = React.useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const client = useApolloClient();

  const [createProductDraft, createProductDraftQuery] =
    useS4HanaKmatProductCreateVariantDraftMutation({
      client,
    });
  const productVariantDraft =
    createProductDraftQuery.data?.sapCreateProductKmatVariantDraft;
  React.useEffect(() => {
    createProductDraft({ variables: { input: { productId } } }).then(result => {
      if (!result.data) {
        throw new Error("Failed to create product variant draft");
      }
      setProperties(result.data.sapCreateProductKmatVariantDraft.inputProps);
    });
  }, [createProductDraft, productId]);

  const [setProductVariantPropValue, setProductVariantPropValueMutation] =
    useS4HanaKmatProductSetVariantPropValueMutation({ client });

  const handleChange = React.useCallback(
    async (prop: { key: string; value: string | null }) => {
      setIsUpdating(true);
      try {
        if (!productVariantDraft) {
          throw new Error("Product variant draft missing");
        }
        const result = await setProductVariantPropValue({
          variables: {
            input: {
              baseProductId: productVariantDraft.baseProductId,
              productDraftUuid: productVariantDraft.productDraftUuid,
              prop,
            },
          },
        });

        if (!result.data) {
          throw new Error(" Failed to set prouct variant prop value");
        }

        setProperties(
          result.data.sapSetProductKmatVariantDraftPropValue.inputProps
        );
      } catch (e) {
        if (e instanceof Error)
          enqueueSnackbar(e.message, { variant: "error" });
      } finally {
        setIsUpdating(false);
      }
    },
    [enqueueSnackbar, productVariantDraft, setProductVariantPropValue]
  );

  const [activateProductVariant] = useS4HanaKmatProductActivateVariantMutation({
    client,
  });

  const [s4HanaKmatProductSetItemProductFromSapProduct] =
    useS4HanaKmatProductSetItemProductFromSapProductMutation({ client });

  const handleSubmit = async (values: FormValues) => {
    if (!productVariantDraft) {
      throw new Error("Product variant draft missing");
    }

    const { data } = await activateProductVariant({
      variables: {
        input: {
          baseProductId: productVariantDraft.baseProductId,
          productDraftUuid: productVariantDraft.productDraftUuid,
          netPrice: values.netPrice,
          supplierArticleNumber: values.manufacturerArticleNumber,
        },
      },
    });

    if (!data) {
      throw new Error("Failed to activate product variant");
    }

    await s4HanaKmatProductSetItemProductFromSapProduct({
      variables: {
        input: {
          projectId,
          quoteId,
          itemId,
          sapProductId:
            data.sapCreateProductFromProductKmatVariantDraft.productId,
        },
      },
      refetchQueries,
    });

    handleClose();
  };

  const formikRef = React.useRef<FormikProps<FormValues>>(null);

  const { initialValues, validationSchema } = React.useMemo(
    () => ({
      initialValues: {
        manufacturerArticleNumber:
          formikRef.current?.values?.manufacturerArticleNumber ??
          initialManufacturerArticleNumber ??
          "",
        netPrice: formikRef.current?.values?.netPrice ?? initialNetPrice ?? 0,
        ...(properties
          ? properties.reduce(
              (acc, property) => ({
                ...acc,
                [property.prop.key]: property.prop,
              }),
              {}
            )
          : undefined),
      } as FormValues,
      validationSchema: Yup.object()
        .shape({
          manufacturerArticleNumber: Yup.string()
            .label(t("Manufacturer article nr", { ns: "SapS4Hana" }))
            .required()
            .max(MANUFACTURER_ARTICLE_NUMBER_MAX_LENGTH),
          netPrice: Yup.number()
            .label(t("Factory price", { ns: "SapS4Hana" }))
            .nullable()
            .required()
            .moreThan(0),
        })
        .shape(
          properties
            ? properties.reduce(
                (acc, property) => ({
                  ...acc,
                  [property.prop.key]: createPropertyValidationSchema(
                    property,
                    getNumberLabel,
                    t
                  ),
                }),
                {}
              )
            : {}
        ),
    }),
    [
      getNumberLabel,
      initialManufacturerArticleNumber,
      initialNetPrice,
      properties,
      t,
    ]
  );

  const formId = React.useId();

  return (
    <Modal
      title={t("Configure product", { ns: "SapS4Hana" })}
      handleClose={handleClose}
      isLoading={
        !createProductDraftQuery.called || createProductDraftQuery.loading
      }
      actionButtons={[
        {
          label: t("Submit", { ns: "Global" }),
          buttonProps: {
            type: "submit",
            form: formId,
            disabled: isUpdating || !isValid,
            loading: isSubmitting,
          },
        },
      ]}
    >
      {properties && (
        <Formik<FormValues>
          innerRef={formikRef}
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          validateOnMount
          enableReinitialize={true}
        >
          {formikProps => (
            <Form id={formId} style={{ position: "relative" }}>
              <Stack spacing={1}>
                <TextField
                  label={t("Manufacturer article nr", { ns: "SapS4Hana" })}
                  name="manufacturerArticleNumber"
                  required
                />
                <FormattedPriceField
                  name="netPrice"
                  label={t("Factory price", { ns: "SapS4Hana" })}
                  type="price"
                  required
                />

                {properties.map(property => (
                  <Stack spacing={1} key={property.prop.key}>
                    {property.textPropFormat === "DATE" ? (
                      <DatePickerField
                        name={`${property.prop.key}.valueText`}
                        label={property.prop.label}
                        onChange={(event, value) => {
                          formikProps.setFieldValue(
                            `${property.prop.key}.valueText`,
                            value?.toString()
                          );
                        }}
                      />
                    ) : property.textPropFormat === "TIME" ? (
                      <TimePickerField
                        name={`${property.prop.key}.valueText`}
                        label={property.prop.label}
                      />
                    ) : (
                      <WizardPropertyField
                        key={property.prop.key}
                        name={property.prop.key}
                        property={property.prop}
                        required={property.isRequired}
                        disabled={
                          property.isReadonly ||
                          setProductVariantPropValueMutation.loading
                        }
                        valueLabels={property.valueLabels}
                        showSelectThreshold={0}
                        additionalRanges={property.additionalRanges}
                      />
                    )}
                  </Stack>
                ))}
              </Stack>
              {isUpdating && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    pointerEvents: "none",
                    backgroundColor: "white",
                    opacity: 0.7,
                    zIndex: 1,
                  }}
                >
                  <Box sx={{ display: "flex", height: "100%" }}>
                    <LoadingSpinner sx={{ opacity: 1 }} />
                  </Box>
                </Box>
              )}
              <FormikChangeObserver
                handleChange={handleChange}
                setValidity={setValidity}
                setIsSubmitting={setIsSubmitting}
              />
            </Form>
          )}
        </Formik>
      )}
    </Modal>
  );
};

const FormikChangeObserver = ({
  handleChange,
  setValidity,
  setIsSubmitting,
}: {
  handleChange: (prop: { key: string; value: string | null }) => Promise<void>;
  setValidity: (isValid: boolean) => void;
  setIsSubmitting: (isSubmitting: boolean) => void;
}) => {
  const { values, initialValues, isValid, isSubmitting } =
    useFormikContext<FormValues>();

  const handleDebouncedChange = React.useMemo(
    () => debounce(handleChange, 1000, { leading: false, trailing: true }),
    [handleChange]
  );

  React.useEffect(() => {
    const diff = differenceWith(
      Object.entries(omit(values, "netPrice", "manufacturerArticleNumber")),
      Object.entries(
        omit(initialValues, "netPrice", "manufacturerArticleNumber")
      ),
      isEqual
    );

    if (diff.length > 0) {
      const prop = diff[0][1];
      handleDebouncedChange({
        key: diff[0][0],
        value:
          prop.__typename === "Props2Text"
            ? prop.valueText || null
            : prop.__typename === "Props2Number"
              ? (prop.valueNumber?.toString() ?? null)
              : prop.__typename === "Props2Bool"
                ? (prop.valueBool?.toString() ?? null)
                : prop.__typename === "Props2NumberArray"
                  ? (prop.valueNumberArray?.join(",") ?? null)
                  : prop.__typename === "Props2TextArray"
                    ? (prop.valueTextArray?.join(",") ?? null)
                    : assertNever(prop),
      });
    }
  }, [handleDebouncedChange, initialValues, values]);

  React.useEffect(() => {
    setValidity(isValid);
  }, [isValid, setValidity]);

  React.useEffect(() => {
    setIsSubmitting(isSubmitting);
  }, [isSubmitting, setIsSubmitting]);

  return null;
};

export const getNumberAllowedValueErrorMessage = (
  prop:
    | Pick<Props2Number, "range" | "allowedValuesNumber">
    | Pick<Props2NumberArray, "range" | "allowedValuesNumber">,
  additionalRanges: Props2NumberRange[],
  getNumberLabel: (n: number) => string,
  t: TFunction<"Validation" | "Global">
) => {
  const ranges = [prop.range ?? null, ...additionalRanges].filter(notNull);
  const allowedValues: number[] = (prop.allowedValuesNumber ?? []).map(
    av => av.allowedNumber
  );

  const parts: string[] = [];

  ranges.forEach(range => {
    if (!isNil(range.min) && !isNil(range.max)) {
      parts.push(
        t("be between {min} and {max}", {
          ns: "Validation",
          min: getNumberLabel(range.min),
          max: getNumberLabel(range.max),
        })
      );
    } else if (!isNil(range.min)) {
      parts.push(
        t("be greater than or equal to {min}", {
          ns: "Validation",
          min: getNumberLabel(range.min),
        })
      );
    } else if (!isNil(range.max)) {
      parts.push(
        t("be less than or equal to {max}", {
          ns: "Validation",
          max: getNumberLabel(range.max),
        })
      );
    }
  });

  if (allowedValues.length > 0) {
    parts.push(
      t("be one of: {values}", {
        ns: "Validation",
        values: allowedValues.map(getNumberLabel).join(", "),
      })
    );
  }

  return parts.length > 0
    ? t("Value must {condition}", {
        ns: "Validation",
        condition: parts.join(`, ${t("or", { ns: "Global" })} `),
      })
    : t("Value doesn't match required conditions", { ns: "Validation" });
};

export const validateNumberValue = (
  value: number,
  prop:
    | Pick<Props2Number, "range" | "step" | "allowedValuesNumber">
    | Pick<Props2NumberArray, "range" | "step" | "allowedValuesNumber">,
  additionalRanges: Props2NumberRange[]
) => {
  const step = prop.step ?? null;
  if (prop.range) {
    if (
      (isNull(step) && isValueNumberInNumberRange(value, prop.range)) ||
      (!isNull(step) && isValueNumberInNumberRangeStep(value, prop.range, step))
    ) {
      // number in main range
      return true;
    }
  }
  if (additionalRanges && additionalRanges.length > 0) {
    if (
      additionalRanges.some(range => {
        if (
          (isNull(step) && isValueNumberInNumberRange(value, range)) ||
          (!isNull(step) && isValueNumberInNumberRangeStep(value, range, step))
        ) {
          return true;
        }
        return false;
      })
    ) {
      // number in additional ranges
      return true;
    }
  }
  if (prop.allowedValuesNumber && prop.allowedValuesNumber.length > 0) {
    if (isValueNumberInAllowedValuesNumber(value, prop.allowedValuesNumber)) {
      // number in allowed values
      return true;
    }
  }

  if (
    prop.range ||
    (additionalRanges && additionalRanges.length > 0) ||
    (prop.allowedValuesNumber && prop.allowedValuesNumber.length > 0)
  ) {
    // we have conditions and value didn't match any
    return false;
  }

  return true;
};

export const isDefinedRangeOrAllowedValue = (
  prop:
    | Pick<Props2Number, "range" | "step" | "allowedValuesNumber">
    | Pick<Props2NumberArray, "range" | "step" | "allowedValuesNumber">,
  additionalRanges: Props2NumberRange[]
) => {
  return (
    (prop.range && (!isNull(prop.range.min) || !isNull(prop.range.max))) ||
    (prop.allowedValuesNumber && prop.allowedValuesNumber.length > 0) ||
    (additionalRanges &&
      additionalRanges.length > 0 &&
      additionalRanges.some(range => !isNull(range.min) || !isNull(range.max)))
  );
};

const addNumberAllowedValueSchema = (
  valueSchema: Yup.NumberSchema<
    number | undefined,
    Record<string, any>,
    number | undefined
  >,
  prop: Props2Number | Props2NumberArray,
  additionalRanges: Props2NumberRange[],
  getNumberLabel: (n: number) => string,
  t: TFunction<"Validation" | "Global">
) => {
  return valueSchema.test(
    "allowedValue",
    getNumberAllowedValueErrorMessage(
      prop,
      additionalRanges,
      getNumberLabel,
      t
    ),
    value => {
      if (!isNil(value)) {
        return validateNumberValue(value, prop, additionalRanges);
      }
      return true;
    }
  );
};

export const createPropertyValidationSchema = (
  property: SapProductKmatVariantInputProp,
  getNumberLabel: (n: number) => string,
  t: TFunction<"Validation" | "Global">
) => {
  switch (property.prop.__typename) {
    case "Props2Bool": {
      let valueSchema = Yup.boolean().label(property.prop.label).nullable();
      if (property.isRequired) {
        valueSchema = valueSchema.required();
      }

      return Yup.object().shape({
        valueBool: valueSchema,
      });
    }
    case "Props2Number": {
      let valueSchema = Yup.number().label(property.prop.label);
      if (property.isRequired) {
        valueSchema = valueSchema.required();
      }

      if (
        isDefinedRangeOrAllowedValue(property.prop, property.additionalRanges)
      ) {
        valueSchema = addNumberAllowedValueSchema(
          valueSchema,
          property.prop,
          property.additionalRanges,
          getNumberLabel,
          t
        );
      } else if (property.maxLength) {
        valueSchema = valueSchema.test("length", value =>
          value ? value.toString().length <= property.maxLength! : true
        );
      }

      // @ts-ignore
      valueSchema = valueSchema.nullable();

      return Yup.object().shape({
        valueNumber: valueSchema,
      });
    }
    case "Props2NumberArray": {
      let valueSchema = Yup.number();

      if (
        isDefinedRangeOrAllowedValue(property.prop, property.additionalRanges)
      ) {
        valueSchema = addNumberAllowedValueSchema(
          valueSchema,
          property.prop,
          property.additionalRanges,
          getNumberLabel,
          t
        );
      } else if (property.maxLength) {
        valueSchema = valueSchema.test("length", value =>
          value ? value.toString().length <= property.maxLength! : true
        );
      }

      return Yup.object().shape({
        valueNumberArray: Yup.array(valueSchema)
          .nullable()
          .when([], {
            is: () => property.isRequired,
            then: schema => schema.required().min(1),
          })
          .label(property.prop.label),
      });
    }
    case "Props2Text": {
      let valueSchema = Yup.string().label(property.prop.label).nullable();
      if (property.isRequired) {
        valueSchema = valueSchema.required();
      }
      if (property.prop.allowedValuesText.length > 0) {
        valueSchema = valueSchema.oneOf([
          null,
          ...property.prop.allowedValuesText.map(v => v.allowedText),
        ]);
      } else if (property.maxLength) {
        valueSchema = valueSchema.max(property.maxLength);
      }

      return Yup.object().shape({
        valueText: valueSchema,
      });
    }
    case "Props2TextArray": {
      let valueSchema = Yup.string();
      if (property.prop.allowedValuesText.length > 0) {
        valueSchema = valueSchema.oneOf(
          property.prop.allowedValuesText.map(v => v.allowedText)
        );
      } else if (property.maxLength) {
        valueSchema = valueSchema.max(property.maxLength);
      }

      return Yup.object().shape({
        valueTextArray: Yup.array(valueSchema)
          .nullable()
          .when([], {
            is: () => property.isRequired,
            then: schema => schema.required().min(1),
          })
          .label(property.prop.label),
      });
    }
    default: {
      assertNever(property.prop);
    }
  }
};
